<script lang="ts" setup>
import {ref} from "vue";
import type {CloudinaryAsset} from "~/types/Contentful";
import {ServerRoutes} from "~/server/enums/server-routes";

import { vIntersectionObserver } from '@vueuse/components'
import VueEasyLightbox from "vue-easy-lightbox";

usePageSeo('seoPhotosTitle', 'seoPhotosDescription');

const showLightbox = ref(false);
const shownImageIndex = ref(0);

const {data} = await useFetch<CloudinaryAsset[]>(ServerRoutes.photos);
const assets: CloudinaryAsset[] = data.value ?? [];

const onHide = () => (showLightbox.value = false);

function expandImage(index: number) {
  shownImageIndex.value = index;
  showLightbox.value = true;
}

//#region Intersection Observer
const loadedImages = ref<Set<number>>(new Set());
const images = computed(() =>
    assets.map(asset => asset.secure_url)
);

function onIntersect(index: number) {
  loadedImages.value.add(index);
}
//#endregion
</script>

<template>
  <div class="photos-page">
    <Hero 
      :title="$t('Photos')" 
      :subtitle="$t('photoSubtitle')"
      backgroundImage="https://res.cloudinary.com/dlct0dnwt/image/upload/q_auto:eco/v1743292000/refuge/2025/MV3A5376_up9lcw.jpg"
    />

    <div class="content-container">
      <ClientOnly>
        <div class="masonry-grid">
          <div v-for="(img, index) of assets"
               :key="index"
               class="grid-item"
               v-intersection-observer="() => onIntersect(index)">
            <button type="button" class="image-wrapper"
                    :aria-label="$t('enlargePhoto')"
                    @click="() => expandImage(index)">
              <nuxt-img v-if="loadedImages.has(index)"
                        :src="img.secure_url"
                        format="webp"
                        quality="60"
                        loading="lazy"
                        :alt="img.context?.custom?.alt || $t('altRefugePhoto')"
              />
              <div v-else class="skeleton-placeholder"></div>
              <div class="overlay">
                <Icon name="ph:magnifying-glass-plus-bold" size="2rem" aria-hidden="true"/>
              </div>
            </button>
          </div>
        </div>

        <VueEasyLightbox
            :visible="showLightbox"
            :imgs="images"
            :index="shownImageIndex"
            @hide="onHide"
            loop
            rotate-disabled
            move-disabled
            close-on-click-outside
        />
      </ClientOnly>
    </div>
  </div>
</template>

<style lang="sass" scoped>
.photos-page
  width: 100vw

.content-container
  max-width: 1400px
  margin: 0 auto
  padding: 0 2rem 8rem

.masonry-grid
  display: grid
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr))
  gap: 1.5rem
  grid-auto-rows: 300px

.grid-item
  border-radius: $border-radius-card
  overflow: hidden
  box-shadow: $shadow-modern
  transition: all $transition-standard

  &:hover
    transform: translateY(-5px)
    box-shadow: $shadow-hover
    
    .overlay
      opacity: 1

.image-wrapper
  width: 100%
  height: 100%
  position: relative
  cursor: pointer
  display: block
  padding: 0
  border: none
  background: none

  img
    width: 100%
    height: 100%
    object-fit: cover

.skeleton-placeholder
  width: 100%
  height: 100%
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)
  background-size: 200% 100%
  animation: loading 1.5s infinite

.dark-mode .skeleton-placeholder
  background: linear-gradient(90deg, #2a2a2a 25%, #333 50%, #2a2a2a 75%)
  background-size: 200% 100%

@keyframes loading
  0%
    background-position: 200% 0
  100%
    background-position: -200% 0

.overlay
  position: absolute
  inset: 0
  background-color: rgba(0, 0, 0, 0.3)
  display: flex
  align-items: center
  justify-content: center
  color: white
  opacity: 0
  transition: opacity $transition-fast

@media (max-width: 768px)
  .masonry-grid
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr))
    grid-auto-rows: 200px
</style>
